import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import AuthService from '../../../services/auth.service';
import { Spinner, Alert } from '../../../services/notiflix.service';
import { useDispatch, useSelector } from 'react-redux';
import { login } from '../../../actions/auth';
import AuthCommonButton from '../../../components/common/auth/AuthCommonButton';
import { CloseEye, OpenEye } from '../../../components/common/IconProvider';
import SocialMediaButton from '../../../components/common/auth/SocialMediaButton';
import TwoStepVerificationOtp from '../../../components/common/auth/TwoStepVerificationOtp';
import AuthRootComponent from '../../../components/common/auth/AuthRootComponent';
import AuthHeaderTextComponent from '../../../components/common/auth/AuthHeaderTextComponent';
import TurnstileWidget from '../../../components/common/auth/TurnstileWidget';
import { useTranslation } from 'react-i18next';
import i18n from '../../../utils/i18n';
import { changeLanguage } from '../../../actions/langauge';
import AccountSettingsService from '../../../services/account-settings.service';
import configService from '../../../services/config.service';
const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  //
  const language = useSelector((state) => state.languageReducer.language);

  useEffect(() => {
    i18n.changeLanguage(language);
  }, [language]);
  //

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const code = queryParams.get('code');

  const [authUser, setAuthUser] = useState({});
  const [passwordShown, setPasswordShown] = useState(false);
  const { t } = useTranslation(['profile', 'auth', 'common']);
  const schema = Yup.object({
    email: Yup.string()
      .required(t('auth:email_required'))
      .email(t('auth:email_invalid')),
    password: Yup.string().required(t('auth:password_required')),
  }).required();

  const [showAuthenticator, setShowAuthenticator] = useState(false);
  const [isLoader, setLoader] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);
  const turnstileEnabled = !!configService.turnstileSiteKey();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    const isValidSocialAuthToken = async () => {
      if (code) {
        Spinner.show();
        try {
          let response = await AuthService.isValidSocialAuthToken(code);
          if (response.status === 'failure') {
            setLoader(false);
            Spinner.hide();
            Alert.error(response.message);
            return false;
          }

          const userDetails = JSON.parse(response?.data);

          let authUser = {
            userId: userDetails?.id,
            email: userDetails?.email,
            profilePicture: userDetails?.profilePicture,
            role: userDetails?.role,
            token: userDetails?.token,
            userName: userDetails?.full_name,
            kyc_status: userDetails?.kyc_status,
            platform_agreement_status: userDetails?.platform_agreement_status,
            email_verified_at: userDetails?.email_verified_at,
            is2FAEnabled: userDetails?.is_two_factor_enabled,
            isChangePassword: userDetails?.is_change_password,
            chatwootIDHash: userDetails?.chatwootIDHash,
            phoneNumber: userDetails?.phone_number,
          };
          let _is2FAEnabled = userDetails?.is_two_factor_enabled;

          setAuthUser(authUser);
          setLoader(false);
          Spinner.hide();
          if (_is2FAEnabled === 1) {
            setLoader(false);
            setShowAuthenticator(true);
            return false;
          } else {
            await Promise.resolve(dispatch(login(authUser)));
            var userLang = navigator.language || navigator.userLanguage;
            let langUpdateResponse =
              await AccountSettingsService.updateUserLocalLang({
                lang: userLang === 'nl' ? 'nl' : 'en',
              });

            if (langUpdateResponse.status === 'failure') {
              Alert.error(langUpdateResponse.message);
            }
            i18n.changeLanguage(userLang === 'nl' ? 'nl' : 'en');
            await Promise.resolve(
              dispatch(changeLanguage(userLang === 'nl' ? 'nl' : 'en')),
            );
            navigate('/dashboard');
          }
          Spinner.hide();
        } catch (error) {
          Spinner.hide();
          Alert.error(error.message);
        }
      }
    };
    isValidSocialAuthToken();
  }, [code]);

  const onLogin = async (_input) => {
    try {
      if (turnstileEnabled && !turnstileToken) {
        Alert.error(t('auth:captcha_required'));
        return false;
      }
      setLoader(true);
      const loginData = {
        email: _input.email,
        password: _input.password,
        'cf-turnstile-response': turnstileToken,
      };

      let response = await AuthService.login(loginData);
      if (response.status === 'failure') {
        setLoader(false);
        setTurnstileToken(null);
        turnstileRef.current?.reset();
        Alert.error(response.message);
        return false;
      }

      let authUser = {
        userId: response.data.id,
        email: response.data.email,
        profilePicture: response.data.profilePicture,
        role: response.data.role,
        token: response.data.token,
        userName: response.data.full_name,
        kyc_status: response.data.kyc_status,
        platform_agreement_status: response.data.platform_agreement_status,
        email_verified_at: response.data.email_verified_at,
        is2FAEnabled: response.data.is_two_factor_enabled,
        isChangePassword: response.data.is_change_password,
        user_wallets: response.data.user_wallets,
        chatwootIDHash: response.data.chatwootIDHash,
        phoneNumber: response.data.phone_number,
      };
      let _is2FAEnabled = response.data.is_two_factor_enabled;
      setAuthUser(authUser);
      setLoader(false);
      if (_is2FAEnabled === 1) {
        setLoader(false);
        setShowAuthenticator(true);
        return false;
      } else {
        await Promise.resolve(dispatch(login(authUser)));
        var userLang = navigator.language || navigator.userLanguage;
        let langUpdateResponse =
          await AccountSettingsService.updateUserLocalLang({
            lang: userLang === 'nl' ? 'nl' : 'en',
          });

        if (langUpdateResponse.status === 'failure') {
          Alert.error(langUpdateResponse.message);
        }
        i18n.changeLanguage(userLang === 'nl' ? 'nl' : 'en');
        await Promise.resolve(
          dispatch(changeLanguage(userLang === 'nl' ? 'nl' : 'en')),
        );

        navigate('/dashboard');
      }
    } catch (error) {
      setLoader(false);
      setTurnstileToken(null);
      turnstileRef.current?.reset();
      Alert.error(error.message);
      return false;
    }
  };

  return (
    <AuthRootComponent>
      {showAuthenticator === false && (
        <div className="w-full md:w-96 bg-white dark:bg-slate-800  dark:text-slate-200 px-6 py-10 rounded-md m-6">
          <AuthHeaderTextComponent
            title={t('common:welcome_message', {
              appName: configService.appName(),
            })}
            desc={t('auth:login_with_credentials')}
          />
          <form onSubmit={handleSubmit(onLogin)}>
            <input
              placeholder={t('common:email_address')}
              {...register('email', { required: true })}
              className="mb-1"
              type="email"
            />
            <div className="common-error-text">{errors.email?.message}</div>
            <div className="relative mb-2 mt-6">
              <input
                placeholder={t('common:password')}
                {...register('password', {
                  required: true,
                })}
                className="mb-1"
                type={passwordShown ? 'text' : 'password'}
              />
              <div
                onClick={() => setPasswordShown(passwordShown ? false : true)}
              >
                <div className="absolute right-0 top-1 w-10 h-10 flex justify-center items-center">
                  {passwordShown ? <CloseEye /> : <OpenEye />}
                </div>
              </div>
              <div className="common-error-text">
                {errors.password?.message}
              </div>
            </div>

            <div>
              <Link
                to="/forgot-password"
                className={`text-[#3D4CB2] dark:text-GreenColor text-sm ${
                  isLoader ? 'pointer-events-none text-gray-400' : ''
                }`}
              >
                {t('auth:forgot_password')}
              </Link>
            </div>
            <TurnstileWidget
              ref={turnstileRef}
              onVerify={setTurnstileToken}
              onExpire={() => setTurnstileToken(null)}
            />
            <div className="mt-4">
              <AuthCommonButton
                isLoader={isLoader}
                Title={'Login'}
                isDisabled={turnstileEnabled && !turnstileToken}
              />
            </div>
          </form>
          <div className="flex items-center md:justify-between justify-center my-3 flex-wrap">
            <div className="text-[#85919F] text-center text-sm">
              {t('auth:dont_have_account')}
            </div>

            <div className="text-center">
              <Link
                to="/signup"
                className={`text-[#3D4CB2] dark:text-GreenColor text-center inline-block text-sm ${
                  isLoader ? 'pointer-events-none text-gray-400' : ''
                }`}
              >
                {t('auth:create_new_account')}
              </Link>
            </div>
          </div>
          <SocialMediaButton />
        </div>
      )}
      {/* OTP Verification */}
      {showAuthenticator && <TwoStepVerificationOtp authUser={authUser} />}

      {/* OTP Verification */}
    </AuthRootComponent>
  );
};

export default Login;
